import { useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { X, Upload, Download, FileSpreadsheet, CheckCircle, AlertTriangle } from "lucide-react";

/**
 * Reusable CSV/XLSX bulk-import modal.
 *
 * Props:
 *   - title (string)
 *   - description (string)
 *   - uploadEndpoint (string)    e.g. "/vehicles/bulk-import"
 *   - templateEndpoint (string)  e.g. "/vehicles/bulk-import/template"
 *   - onClose ()
 *   - onSuccess (result) — result is the backend response body
 *   - resultLabels { createdKey, skippedKey } — optional override of fields in result
 */
export default function BulkImportModal({
  title,
  description,
  uploadEndpoint,
  templateEndpoint,
  onClose,
  onSuccess,
  resultLabels = { createdKey: "created", updatedKey: "updated", skippedKey: "skipped" },
}) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const pick = () => inputRef.current?.click();
  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult(null);
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get(templateEndpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const disp = resp.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(disp);
      a.download = m ? m[1] : "template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(uploadEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" data-testid="bulk-import-modal">
      <div className="bg-[var(--bone)] border border-[var(--ink-200)] max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ink-200)]">
          <div className="font-display text-xl tracking-tight">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--surface-alt)]" data-testid="bulk-import-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-[var(--ink-700)]">{description}</div>

          <button onClick={downloadTemplate} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="bulk-import-template-btn">
            <Download size={12} /> {t("bulk.downloadTemplate")}
          </button>

          {!result && (
            <>
              <div
                className="border-2 border-dashed border-[var(--ink-200)] p-8 text-center cursor-pointer hover:bg-[var(--surface-alt)]"
                onClick={pick}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) { setFile(f); setResult(null); }
                }}
                data-testid="bulk-import-dropzone"
              >
                <FileSpreadsheet size={28} className="mx-auto text-[var(--ink-500)] mb-2" />
                <div className="text-sm">{file ? file.name : t("bulk.dropHint")}</div>
                <div className="label-tech mt-1">CSV · XLSX</div>
              </div>
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={onPick} data-testid="bulk-import-file-input" />

              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-ghost" data-testid="bulk-import-cancel">{t("admin.orgs.cancel")}</button>
                <button onClick={submit} disabled={!file || uploading} className="btn-primary" data-testid="bulk-import-submit">
                  <Upload size={14} /> {uploading ? t("bulk.uploading") : t("bulk.upload")}
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3" data-testid="bulk-import-result">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {result.invited !== undefined ? (
                  <ResultStat icon={CheckCircle} tint="var(--moss)" label={t("bulk.invited")} value={result.invited ?? 0} testid="bulk-result-invited" />
                ) : (
                  <>
                    <ResultStat icon={CheckCircle} tint="var(--moss)" label={t("bulk.created")} value={result[resultLabels.createdKey] ?? 0} testid="bulk-result-created" />
                    {result[resultLabels.updatedKey] !== undefined && (
                      <ResultStat icon={CheckCircle} tint="var(--clay)" label={t("bulk.updated")} value={result[resultLabels.updatedKey] ?? 0} testid="bulk-result-updated" />
                    )}
                  </>
                )}
                <ResultStat icon={AlertTriangle} tint="var(--ink-500)" label={t("bulk.skipped")} value={result[resultLabels.skippedKey] ?? 0} testid="bulk-result-skipped" />
                <ResultStat icon={AlertTriangle} tint="var(--brick)" label={t("bulk.errors")} value={(result.errors || []).length} testid="bulk-result-errors" />
              </div>

              {(result.errors || []).length > 0 && (
                <div className="border border-[var(--brick)]/30 p-3 bg-[var(--brick)]/5">
                  <div className="label-tech text-[var(--brick)] mb-2">{t("bulk.errorsTitle")}</div>
                  <div className="max-h-40 overflow-auto text-xs space-y-1">
                    {result.errors.map((e, idx) => (
                      <div key={idx} className="font-mono-tech">
                        <span className="text-[var(--ink-500)]">{t("bulk.row")} {e.row}:</span> {e.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(result.created_users || []).length > 0 && (
                <div className="border border-[var(--ink-200)] p-3 bg-[var(--surface-alt)]" data-testid="bulk-result-temp-passwords">
                  <div className="label-tech mb-2">{t("bulk.tempPasswords")}</div>
                  <div className="text-xs text-[var(--ink-500)] mb-2">{t("bulk.tempPasswordsHelp")}</div>
                  <div className="max-h-48 overflow-auto text-xs space-y-1">
                    {result.created_users.map((u, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 py-1 border-b border-[var(--ink-200)] last:border-0">
                        <span className="truncate">{u.email}</span>
                        {u.temp_password && <span className="font-mono-tech bg-[var(--bone)] px-2 py-0.5 border border-[var(--ink-200)]">{u.temp_password}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button onClick={() => { setFile(null); setResult(null); }} className="btn-ghost" data-testid="bulk-import-another">{t("bulk.uploadAnother")}</button>
                <button onClick={onClose} className="btn-primary" data-testid="bulk-import-done">{t("admin.createUser.done")}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultStat({ icon: Icon, tint, label, value, testid }) {
  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-3" data-testid={testid}>
      <div className="label-tech inline-flex items-center gap-1" style={{ color: tint }}>
        <Icon size={12} /> {label}
      </div>
      <div className="font-display text-2xl mt-1 tracking-tighter">{value}</div>
    </div>
  );
}
